import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import {
  Avatar,
  Button,
  ConfirmDialog,
  Modal,
  RAISONS_ACCES,
  SettingsActions,
  SettingsCode,
  SettingsEmpty,
  SettingsGroup,
  SettingsInfo,
  SettingsInput,
  SettingsLock,
  SettingsMenu,
  SettingsMessage,
  SettingsPanel,
  SettingsRow,
  SettingsSave,
  SettingsSection,
  SettingsTable,
  SkeletonText,
  StatusBadge,
  Switch,
  useAccesPort,
  useBrandingPort,
  usePersonnesPort,
} from '@taki/ui';
import type { DerogationsDeFamille, MembreVue, NiveauAcces, RoleOrg, SettingsMenuOption } from '@taki/ui';
import { CLE_FAMILLE, deriverPortes, portesEnConflit, portesExplicitesDe } from '../acces.js';
import type { PorteApp, ShellAppEntry } from '../types.js';

/**
 * L'ONGLET « UTILISATEURS » DU HUB PARAMETRES.
 *
 * CE QU'IL FAIT : lister les membres de l'org, changer leur role, les
 * desactiver, leur ouvrir ou leur fermer une app (et, en dessous, une porte
 * precise), en ajouter, en retirer, changer leur adresse.
 *
 * CE QU'IL NE FAIT PAS : de la securite. Les Firestore Security Rules refusent
 * deja tout a qui n'est pas owner ou admin, et refuseraient une promotion en
 * owner venue d'un admin. Ce panneau GRISE ce que les rules refuseraient, avec
 * la raison ecrite (G16 : on explique, on ne cache pas), et affiche le refus
 * quand il arrive quand meme. Il ne remplace jamais les rules.
 *
 * IL NE PARLE PAS AU SDK NON PLUS : le shell n'a pas `@taki/sdk` dans ses
 * dependances, et c'est voulu. Tout passe par le PORT (`AccesPort.membres`),
 * que l'hote remplit (`apps/hello/src/App.tsx`).
 */

/** Les quatre roles d'org, dans l'ordre du plus fort au plus faible. */
const ROLES: ReadonlyArray<{ value: RoleOrg; label: string }> = [
  { value: 'owner', label: 'Proprietaire' },
  { value: 'admin', label: 'Administrateur' },
  { value: 'member', label: 'Membre' },
  { value: 'viewer', label: 'Lecteur' },
];

/** Les trois niveaux d'une porte ou d'une app, du plus ferme au plus ouvert. */
const NIVEAUX: ReadonlyArray<{ value: NiveauAcces; label: string }> = [
  { value: 'none', label: 'Aucun acces' },
  { value: 'read', label: 'Lecture seule' },
  { value: 'write', label: 'Lecture et ecriture' },
];

/**
 * La valeur du menu « Defaut du role ». C'est la chaine VIDE, et pas une
 * quatrieme valeur : « defaut » n'est pas un niveau, c'est l'ABSENCE de
 * derogation. Poser une valeur ecrirait une derogation qui dit la meme chose
 * que le role, et qui survivrait a un changement de role.
 */
const DEFAUT = '';

/**
 * Les entrees du menu de role : « Proprietaire » grise, avec sa raison, pour
 * qui n'est pas owner (les rules refuseraient la promotion).
 */
function optionsDeRole(estOwner: boolean, raisonOwner: string | undefined): SettingsMenuOption[] {
  return ROLES.map((role) =>
    role.value === 'owner' && !estOwner && raisonOwner !== undefined
      ? { value: role.value, label: role.label, inactif: raisonOwner }
      : { value: role.value, label: role.label },
  );
}

/** Les entrees d'un menu d'acces : le defaut (aucune derogation), puis les niveaux. */
function optionsDAcces(libelleDefaut: string): SettingsMenuOption[] {
  return [{ value: DEFAUT, label: libelleDefaut }, ...NIVEAUX.map((niveau) => ({ value: niveau.value, label: niveau.label }))];
}

/** Le libelle d'un niveau, pour les textes d'aide. */
function libelleNiveau(niveau: NiveauAcces): string {
  return NIVEAUX.find((item) => item.value === niveau)?.label ?? niveau;
}

/** Le libelle d'un role, pour le badge de la liste. */
function libelleRole(role: RoleOrg): string {
  return ROLES.find((item) => item.value === role)?.label ?? role;
}

/** Le nom lisible d'une porte : `assets/materiel`, ou `contacts` sans type. */
function nomDePorte(porte: PorteApp): string {
  return porte.type == null || porte.type === '' ? porte.famille : `${porte.famille}/${porte.type}`;
}

/** La clef d'une porte dans `portes[famille]` : le type, ou `*`. */
function clefDePorte(porte: PorteApp): string {
  return porte.type == null || porte.type === '' ? CLE_FAMILLE : porte.type;
}

/**
 * Le message d'un refus d'ecriture. Un `permission-denied` des rules n'est pas
 * une panne : c'est la reponse juste a un geste que ce compte n'a pas le droit
 * de faire, et l'ecran doit le DIRE, en ligne d'etat, jamais dans une alerte.
 *
 * EXPORTE PARCE QU'UN SECOND PANNEAU EN A BESOIN (« Environnement ») : la
 * phrase du refus est un FAIT, et un fait ecrit deux fois est un fait qui
 * divergera. Le jour ou un troisieme panneau la demande, elle demenagera dans
 * un module a elle ; a deux, la recopier couterait plus cher que cet import.
 */
export function messageDErreur(cause: unknown): string {
  const code = typeof cause === 'object' && cause !== null ? String(Reflect.get(cause, 'code') ?? '') : '';
  const texte =
    typeof cause === 'object' && cause !== null ? String(Reflect.get(cause, 'message') ?? '') : String(cause);
  if (code.includes('permission-denied') || texte.includes('permission-denied')) {
    return 'Modification refusée.';
  }
  return texte === '' ? "Echec de l'operation." : texte;
}

/** L'email est-il utilisable ? Minuscules et un `@`, rien de plus subtil. */
function emailValide(email: string): boolean {
  const propre = email.trim().toLowerCase();
  return propre.includes('@') && !propre.startsWith('@') && !propre.endsWith('@');
}

/** Ce que le detail d'un membre tient en cours d'edition, avant enregistrement. */
interface Brouillon {
  email: string;
  displayName: string;
  orgRole: RoleOrg;
  disabled: boolean;
  /** Le choix PAR APP ; une app absente = defaut du role. */
  apps: Record<string, NiveauAcces>;
  /** Les derogations MANUELLES par porte ; une porte absente = defaut ou choix d'app. */
  portes: Record<string, DerogationsDeFamille>;
}

/** Le brouillon d'ouverture d'une fiche : ses portes manuelles s'y demelent. */
function brouillonDe(membre: MembreVue, appEntries: readonly ShellAppEntry[]): Brouillon {
  return {
    email: membre.email,
    displayName: membre.displayName ?? '',
    orgRole: membre.orgRole,
    disabled: membre.disabled,
    apps: { ...membre.apps },
    portes: portesExplicitesDe(membre.apps, membre.portes, appEntries),
  };
}

export interface MembresSettingsPanelProps {
  /** Apps installees, liste COMPLETE : leurs portes sont ce qu'on ouvre ou ferme. */
  appEntries: readonly ShellAppEntry[];
  /** Config runtime : l'org dont on gere les membres. */
  config: TakiConfig;
}

export function MembresSettingsPanel({
  appEntries,
  config,
}: MembresSettingsPanelProps): JSX.Element {
  const acces = useAccesPort();
  // Le nom de l'environnement tel que le client l'a choisi (onglet Environnement),
  // a defaut celui de la config : le meme que le titre de l'onglet.
  const nomEnvironnement = useBrandingPort()?.courant.name ?? config.branding.name;
  const port = acces?.membres ?? null;
  const peutGerer = acces?.peutGererLesMembres === true;
  const estOwner = acces?.role === 'owner';

  const [membres, setMembres] = useState<MembreVue[] | null>(null);
  const personnesPort = usePersonnesPort();
  const [photos, setPhotos] = useState<ReadonlyMap<string, string>>(new Map());
  useEffect(() => {
    if (personnesPort === null || port === null) return;
    let vivant = true;
    void personnesPort.lister().then(
      (liste) => {
        if (vivant) setPhotos(new Map(liste.filter((p) => p.photoURL).map((p) => [p.personId, p.photoURL as string])));
      },
      () => undefined,
    );
    return () => {
      vivant = false;
    };
  }, [personnesPort, port]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [brouillon, setBrouillon] = useState<Brouillon | null>(null);
  const [enregistre, setEnregistre] = useState(false);
  /** Une fiche part au serveur : le bouton tourne, « Enregistrement… » (`SettingsSave`). */
  const [occupe, setOccupe] = useState(false);
  const [ajoutOuvert, setAjoutOuvert] = useState(false);
  const [aRetirer, setARetirer] = useState<MembreVue | null>(null);
  /* La personne dont on change l'adresse (la fenetre est ouverte), ou `null`. */
  const [aReadresser, setAReadresser] = useState<MembreVue | null>(null);
  /* Le mot de passe qu'on vient de tirer, montre UNE fois et jamais relu. */
  const [motDePasse, setMotDePasse] = useState<{ email: string; valeur: string } | null>(null);
  /* L'adresse dont le mot de passe est en cours de tirage (le bouton tourne). */
  const [tirageEnCours, setTirageEnCours] = useState<string | null>(null);
  /* Un avertissement qui n'est PAS un echec : le geste a marche, en partie. */
  const [avertissement, setAvertissement] = useState<string | null>(null);

  /* Les comptes sont-ils branches ? Sans Cloud Functions, l'hote ne fournit pas
     le geste, et l'ecran le GRISE avec sa raison au lieu de le cacher (G16). */
  const comptesBranches = port?.motDePasse != null;
  const raisonSansComptes =
    'Mots de passe indisponibles ici.';

  /* CHANGER L'ADRESSE : branche par l'hote, ou grise avec sa raison (G16). Sa
     PROPRE adresse ne se change pas d'ici : l'ancienne fiche est archivee dans
     le meme geste, et l'acces de la session en cours tomberait avec elle. */
  const adresseBranchee = port?.changerAdresse != null;
  const raisonAdresse = (membre: MembreVue): string | null =>
    !adresseBranchee
      ? 'Changement d’adresse indisponible ici.'
      : acces?.email != null && acces.email === membre.email
        ? 'Ta propre adresse se change par un autre administrateur.'
        : null;

  /* La liste, lue une fois par port (donc une fois par utilisateur connecte). */
  useEffect(() => {
    if (port === null || !peutGerer) return;
    let vivant = true;
    setMembres(null);
    void port.lister().then(
      (liste) => {
        if (!vivant) return;
        setMembres(liste);
        setErreur(null);
      },
      (cause: unknown) => {
        if (!vivant) return;
        setMembres([]);
        setErreur(messageDErreur(cause));
      },
    );
    return () => {
      vivant = false;
    };
  }, [port, peutGerer]);

  /* CE QUI SERA ECRIT, calcule a chaque frappe : le detail l'affiche en clair,
     pour qu'on voie l'effet d'un choix d'app AVANT d'enregistrer. */
  const portesFinales = useMemo(
    () =>
      brouillon === null
        ? {}
        : deriverPortes(brouillon.apps, brouillon.portes, appEntries),
    [brouillon, appEntries],
  );
  const conflits = useMemo(
    () =>
      brouillon === null ? [] : portesEnConflit(brouillon.apps, appEntries, brouillon.portes),
    [brouillon, appEntries],
  );

  /* Les portes de TOUTES les apps installees, sans doublon : le second etage
     du detail. Une porte partagee par deux apps n'a qu'une ligne. */
  const toutesLesPortes = useMemo<PorteApp[]>(() => {
    const vues = new Set<string>();
    const portes: PorteApp[] = [];
    for (const entry of appEntries) {
      for (const porte of entry.portes) {
        const nom = nomDePorte(porte);
        if (vues.has(nom)) continue;
        vues.add(nom);
        portes.push(porte);
      }
    }
    return portes.sort((a, b) => nomDePorte(a).localeCompare(nomDePorte(b)));
  }, [appEntries]);

  /* ------------------------------------------------------------------ */
  /* Le panneau ferme : on DIT pourquoi, on ne cache pas l'onglet.        */
  /* ------------------------------------------------------------------ */
  if (port === null || !peutGerer) {
    return (
      <SettingsPanel>
        <SettingsSection
          title="Accès"
          contextLabel="Organisation"
          lock={port === null ? 'Gestion indisponible dans cet environnement.' : RAISONS_ACCES.gestionReservee}
        >
          <SettingsEmpty>Gestion des utilisateurs indisponible</SettingsEmpty>
        </SettingsSection>
      </SettingsPanel>
    );
  }

  function majBrouillon(patch: Partial<Brouillon>): void {
    setEnregistre(false);
    setBrouillon((actuel) => (actuel === null ? null : { ...actuel, ...patch }));
  }

  function choisirApp(appId: string, valeur: string): void {
    if (brouillon === null) return;
    const apps = { ...brouillon.apps };
    if (valeur === DEFAUT) delete apps[appId];
    else apps[appId] = valeur as NiveauAcces;
    majBrouillon({ apps });
  }

  function choisirPorte(porte: PorteApp, valeur: string): void {
    if (brouillon === null) return;
    const clef = clefDePorte(porte);
    const portes: Record<string, DerogationsDeFamille> = {};
    for (const [famille, derogations] of Object.entries(brouillon.portes)) {
      portes[famille] = { ...derogations };
    }
    const famille = portes[porte.famille] ?? {};
    if (valeur === DEFAUT) delete famille[clef];
    else famille[clef] = valeur as NiveauAcces;
    if (Object.keys(famille).length === 0) delete portes[porte.famille];
    else portes[porte.famille] = famille;
    majBrouillon({ portes });
  }

  function enregistrer(): void {
    if (brouillon === null || port === null || occupe) return;
    const email = brouillon.email;
    setErreur(null);
    setEnregistre(false);
    setOccupe(true);
    void port
      .modifier(email, {
        displayName: brouillon.displayName.trim(),
        orgRole: brouillon.orgRole,
        disabled: brouillon.disabled,
        apps: brouillon.apps,
        portes: deriverPortes(brouillon.apps, brouillon.portes, appEntries),
      })
      .then(
        (maj) => {
          setMembres((liste) =>
            liste === null ? liste : liste.map((membre) => (membre.email === email ? maj : membre)),
          );
          setBrouillon(brouillonDe(maj, appEntries));
          setEnregistre(true);
        },
        (cause: unknown) => setErreur(messageDErreur(cause)),
      )
      .finally(() => setOccupe(false));
  }

  function retirer(membre: MembreVue): void {
    if (port === null) return;
    setARetirer(null);
    setErreur(null);
    setAvertissement(null);
    void port.retirer(membre.email).then(
      (resultat) => {
        setMembres((liste) =>
          liste === null ? liste : liste.filter((autre) => autre.email !== membre.email),
        );
        if (brouillon?.email === membre.email) setBrouillon(null);
        /* L'ACCES EST TOMBE DANS TOUS LES CAS : les regles du serveur ne
           donnent plus rien a une fiche archivee, tout de suite. Ce qui a pu
           echouer, c'est la coupure des SESSIONS deja ouvertes, qui demande une
           Cloud Function. Le dire, plutot que laisser croire a l'un ou a
           l'autre. */
        if (!resultat.sessionsCoupees) {
          setAvertissement(
            `Accès retiré. Une session ouverte peut durer jusqu’à 1 h (${resultat.raison ?? 'Cloud Function indisponible'}).`,
          );
        }
      },
      (cause: unknown) => setErreur(messageDErreur(cause)),
    );
  }

  /**
   * CHANGER L'ADRESSE D'UNE PERSONNE : une nouvelle fiche au meme `personId`,
   * l'ancienne archivee. La ligne de l'ancienne adresse disparait de la liste,
   * celle de la nouvelle y entre, « sans compte » tant que la personne n'est
   * pas entree avec elle.
   */
  function changerAdresse(membre: MembreVue, nouvelle: string): void {
    const geste = port?.changerAdresse;
    if (geste == null) return;
    setAReadresser(null);
    setErreur(null);
    setAvertissement(null);
    void geste(membre.email, nouvelle).then(
      (resultat) => {
        setMembres((liste) =>
          liste === null
            ? liste
            : [...liste.filter((autre) => autre.email !== membre.email), resultat.membre].sort(
                (a, b) => a.email.localeCompare(b.email),
              ),
        );
        if (brouillon?.email === membre.email) setBrouillon(null);
        /* LA NOUVELLE FICHE EST EN PLACE DANS TOUS LES CAS, et l'ancienne
           archivee : les regles ne lui donnent plus rien. Ce qui a pu echouer,
           c'est le retrait du compte de l'ancienne adresse (une Cloud
           Function) : une session ouverte avec elle vit encore un peu. */
        setAvertissement(
          resultat.sessionsCoupees
            ? `Adresse changee : ${resultat.membre.email}.`
            : `Adresse changée : ${resultat.membre.email}. Une session ouverte peut durer jusqu’à 1 h (${resultat.raison ?? 'Cloud Function indisponible'}).`,
        );
      },
      (cause: unknown) => setErreur(messageDErreur(cause)),
    );
  }

  /**
   * TIRER UN MOT DE PASSE, et creer le compte s'il n'existait pas.
   *
   * C'est le deuxieme chemin d'entree, pour qui n'a pas de compte Google. Le
   * mot de passe n'existe QUE dans la reponse : il n'est ni en base, ni dans un
   * journal, et il ne se relit pas. D'ou la fenetre qui l'affiche, et d'ou le
   * fait qu'elle ne se rouvre jamais.
   */
  function tirerMotDePasse(email: string): void {
    const geste = port?.motDePasse;
    if (geste == null) return;
    setErreur(null);
    setAvertissement(null);
    setTirageEnCours(email);
    void geste(email).then(
      (resultat) => {
        setTirageEnCours(null);
        setMotDePasse({ email, valeur: resultat.motDePasse });
        /* Le compte vient d'etre cree : la pastille « sans compte » de la ligne
           ment desormais. On la corrige sans relire toute la liste. */
        if (resultat.compteCree) {
          setMembres((liste) =>
            liste === null
              ? liste
              : liste.map((membre) =>
                  membre.email === email ? { ...membre, userId: 'pose' } : membre,
                ),
          );
        }
      },
      (cause: unknown) => {
        setTirageEnCours(null);
        setErreur(messageDErreur(cause));
      },
    );
  }

  const raisonOwner = estOwner
    ? undefined
    : 'Réservé aux propriétaires.';
  const raisonAdmin = 'Accès complet pour ce rôle.';
  const grisePortes = brouillon?.orgRole === 'owner' || brouillon?.orgRole === 'admin';

  return (
    <SettingsPanel>
      <SettingsSection
        title="Accès"
        contextLabel={nomEnvironnement}
      >
        {erreur != null && <SettingsMessage tone="error">{erreur}</SettingsMessage>}

        {avertissement != null && <SettingsMessage>{avertissement}</SettingsMessage>}

        {membres === null ? (
          <SkeletonText lines={3} />
        ) : membres.length === 0 ? (
          <SettingsEmpty>Aucun membre : ajoute le premier.</SettingsEmpty>
        ) : (
          <SettingsTable
            label="Utilisateurs"
            columns={[
              { label: 'Utilisateur' },
              { label: 'Rôle' },
              { label: 'État' },
              { label: 'Actions', cache: true, fin: true },
            ]}
            rows={membres.map((membre) => {
              const ouvert = brouillon?.email === membre.email;
              const raisonDAdresse = raisonAdresse(membre);
              return {
                id: membre.email,
                cells: [
                  <span key="utilisateur" className="flex min-w-0 items-center gap-2.5">
                    <Avatar
                      size={28}
                      src={membre.personId ? photos.get(membre.personId) : undefined}
                      initials={(membre.displayName?.trim() || membre.email).charAt(0).toLocaleUpperCase('fr')}
                      className="bg-surface-3 text-ink-700"
                    />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-[0.8125rem] font-medium text-ink-900">
                        {membre.displayName != null && membre.displayName !== ''
                          ? membre.displayName
                          : membre.email}
                      </span>
                      <span className="truncate text-[0.8125rem] text-ink-500">{membre.email}</span>
                    </span>
                  </span>,
                  <StatusBadge key="role" label={libelleRole(membre.orgRole)} tone="accent" />,
                  <StatusBadge
                    key="etat"
                    label={
                      membre.disabled
                        ? 'desactive'
                        : membre.userId == null || membre.userId === ''
                          ? 'sans compte'
                          : 'actif'
                    }
                    tone={
                      membre.disabled ? 'danger' : membre.userId == null || membre.userId === '' ? 'neutral' : 'success'
                    }
                  />,
                  <Button
                    key="modifier"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setEnregistre(false);
                      setBrouillon(ouvert ? null : brouillonDe(membre, appEntries));
                    }}
                  >
                    {ouvert ? 'Fermer' : 'Modifier'}
                  </Button>,
                ],
                detail:
                  ouvert && brouillon !== null ? (
                    <>
                      <SettingsGroup title="Profil">
                        <SettingsRow label="Nom">
                          <SettingsInput
                            value={brouillon.displayName}
                            onChange={(event) => majBrouillon({ displayName: event.target.value })}
                            aria-label={`Nom de ${membre.email}`}
                          />
                        </SettingsRow>

                        <SettingsRow
                          label="Rôle"
                          lock={estOwner ? undefined : (raisonOwner ?? 'Propriétaires uniquement')}
                        >
                          <SettingsMenu
                            label={`Role de ${membre.email}`}
                            value={brouillon.orgRole}
                            options={optionsDeRole(estOwner, raisonOwner)}
                            onChange={(valeur) => majBrouillon({ orgRole: valeur as RoleOrg })}
                          />
                        </SettingsRow>

                        <SettingsRow label="Compte désactivé">
                          <Switch
                            checked={brouillon.disabled}
                            onChange={(checked) => majBrouillon({ disabled: checked })}
                            aria-label={`Desactiver ${membre.email}`}
                          />
                        </SettingsRow>
                      </SettingsGroup>

                      <SettingsGroup title="Accès par app">
                        {appEntries.length === 0 ? (
                          <SettingsEmpty>Aucune app installee dans cette organisation.</SettingsEmpty>
                        ) : (
                          appEntries.map((entry) => (
                            <SettingsRow
                              key={entry.id}
                              label={entry.label}
                              lock={grisePortes ? raisonAdmin : undefined}
                            >
                              <SettingsMenu
                                label={`Acces de ${membre.email} a ${entry.label}`}
                                value={brouillon.apps[entry.id] ?? DEFAUT}
                                options={optionsDAcces('Defaut du role')}
                                disabled={grisePortes}
                                onChange={(valeur) => choisirApp(entry.id, valeur)}
                              />
                            </SettingsRow>
                          ))
                        )}
                        {conflits.length > 0 && (
                          <SettingsInfo>{`Accès le plus permissif retenu : ${conflits.join(', ')}.`}</SettingsInfo>
                        )}
                      </SettingsGroup>

                      <SettingsGroup title="Accès par porte">
                        {toutesLesPortes.length === 0 ? (
                          <SettingsEmpty>Aucune app installee ne declare de donnees.</SettingsEmpty>
                        ) : (
                          toutesLesPortes.map((porte) => {
                            const clef = clefDePorte(porte);
                            const finale = portesFinales[porte.famille]?.[clef];
                            return (
                              <SettingsRow
                                key={nomDePorte(porte)}
                                label={nomDePorte(porte)}
                                hint={
                                  finale === undefined
                                    ? 'Accès du rôle'
                                    : `Accès effectif : ${libelleNiveau(finale)}`
                                }
                                lock={grisePortes ? raisonAdmin : undefined}
                              >
                                <SettingsMenu
                                  label={`Acces de ${membre.email} a ${nomDePorte(porte)}`}
                                  value={brouillon.portes[porte.famille]?.[clef] ?? DEFAUT}
                                  options={optionsDAcces('Defaut')}
                                  disabled={grisePortes}
                                  onChange={(valeur) => choisirPorte(porte, valeur)}
                                />
                              </SettingsRow>
                            );
                          })
                        )}
                      </SettingsGroup>

                      <SettingsActions>
                        <SettingsSave busy={occupe} done={enregistre} onSave={enregistrer} />
                        {/* G16 : le bouton est vraiment `disabled`, et un bouton
                            desactive ne recoit pas le survol ; le cadenas a cote
                            porte la raison, au survol comme au focus. */}
                        {!comptesBranches && <SettingsLock label={raisonSansComptes} />}
                        <Button
                          variant="secondary"
                          size="sm"
                          title={comptesBranches ? undefined : raisonSansComptes}
                          disabled={!comptesBranches || tirageEnCours !== null}
                          loading={tirageEnCours === membre.email}
                          onClick={() => tirerMotDePasse(membre.email)}
                        >
                          {membre.userId == null || membre.userId === ''
                            ? 'Creer un mot de passe'
                            : 'Nouveau mot de passe'}
                        </Button>
                        {raisonDAdresse !== null && <SettingsLock label={raisonDAdresse} />}
                        <Button
                          variant="secondary"
                          size="sm"
                          title={raisonDAdresse ?? undefined}
                          disabled={raisonDAdresse !== null}
                          onClick={() => setAReadresser(membre)}
                        >
                          Changer l&apos;adresse
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => setARetirer(membre)}>
                          Retirer
                        </Button>
                      </SettingsActions>
                    </>
                  ) : undefined,
              };
            })}
          />
        )}

        <SettingsActions>
          <Button variant="secondary" size="sm" onClick={() => setAjoutOuvert(true)}>
            Ajouter un utilisateur
          </Button>
        </SettingsActions>
      </SettingsSection>

      <AjoutMembreModal
        open={ajoutOuvert}
        estOwner={estOwner}
        raisonOwner={raisonOwner}
        comptesBranches={comptesBranches}
        raisonSansComptes={raisonSansComptes}
        onClose={() => setAjoutOuvert(false)}
        onAjouter={(entree, avecMotDePasse) => {
          if (port === null) return;
          setErreur(null);
          setAvertissement(null);
          /* LA FICHE D'ABORD, TOUJOURS : c'est elle qui donne l'acces, et elle
             s'ecrit sans Cloud Function. Le mot de passe n'est qu'un second
             geste, sur une fiche qui existe deja : s'il echoue, l'invitation
             tient quand meme, et l'ecran le dit. */
          void port.ajouter(entree).then(
            (cree) => {
              setMembres((liste) => (liste === null ? [cree] : [...liste, cree]));
              setAjoutOuvert(false);
              if (avecMotDePasse) tirerMotDePasse(cree.email);
            },
            (cause: unknown) => {
              setErreur(messageDErreur(cause));
              setAjoutOuvert(false);
            },
          );
        }}
      />

      <ChangerAdresseModal
        membre={aReadresser}
        adressesPrises={membres?.map((membre) => membre.email) ?? []}
        onClose={() => setAReadresser(null)}
        onChanger={(nouvelle) => {
          if (aReadresser !== null) changerAdresse(aReadresser, nouvelle);
        }}
      />

      <MotDePasseModal
        entree={motDePasse}
        onClose={() => setMotDePasse(null)}
      />

      <ConfirmDialog
        open={aRetirer !== null}
        tone="danger"
        title="Retirer cet utilisateur ?"
        body={[
          aRetirer?.email ?? '',
          "Accès retiré, fiche archivée.",
        ]}
        confirmLabel="Retirer"
        onConfirm={() => {
          if (aRetirer !== null) retirer(aRetirer);
        }}
        onCancel={() => setARetirer(null)}
      />
    </SettingsPanel>
  );
}

/* -------------------------------------------------------------------------- */
/* Ajouter un utilisateur : la CREATION vit dans un Modal (doctrine gabarit).  */
/* -------------------------------------------------------------------------- */

function AjoutMembreModal({
  open,
  estOwner,
  raisonOwner,
  comptesBranches,
  raisonSansComptes,
  onClose,
  onAjouter,
}: {
  open: boolean;
  estOwner: boolean;
  raisonOwner: string | undefined;
  comptesBranches: boolean;
  raisonSansComptes: string;
  onClose: () => void;
  onAjouter: (
    entree: { email: string; displayName?: string; orgRole: RoleOrg },
    avecMotDePasse: boolean,
  ) => void;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [nom, setNom] = useState('');
  const [role, setRole] = useState<RoleOrg>('member');
  const [avecMotDePasse, setAvecMotDePasse] = useState(false);

  // Le formulaire repart a neuf a chaque ouverture : sinon l'email du
  // precedent ajout attend dans le champ.
  useEffect(() => {
    if (!open) return;
    setEmail('');
    setNom('');
    setRole('member');
    setAvecMotDePasse(false);
  }, [open]);

  const propre = email.trim().toLowerCase();
  const valide = emailValide(propre);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ajouter un utilisateur"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            disabled={!valide}
            onClick={() =>
              onAjouter(
                nom.trim() === ''
                  ? { email: propre, orgRole: role }
                  : { email: propre, displayName: nom.trim(), orgRole: role },
                avecMotDePasse && comptesBranches,
              )
            }
          >
            Ajouter
          </Button>
        </>
      }
    >
      <SettingsRow label="Email">
        <SettingsInput
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="prenom.nom@exemple.lu"
          aria-label="Email du nouvel utilisateur"
        />
      </SettingsRow>
      {email !== '' && !valide && <SettingsMessage tone="error">Adresse email invalide.</SettingsMessage>}

      <SettingsRow label="Nom">
        <SettingsInput
          value={nom}
          onChange={(event) => setNom(event.target.value)}
          placeholder="Prenom Nom"
          aria-label="Nom du nouvel utilisateur"
        />
      </SettingsRow>

      <SettingsRow label="Role">
        <SettingsMenu
          label="Role du nouvel utilisateur"
          value={role}
          options={optionsDeRole(estOwner, raisonOwner)}
          onChange={(valeur) => setRole(valeur as RoleOrg)}
        />
      </SettingsRow>

      {/* LES DEUX CHEMINS D'ENTREE, poses comme un choix et pas comme une
          option technique : c'est la seule decision que l'ajout demande. */}
      <SettingsRow label="Creer un mot de passe">
        <Switch
          checked={avecMotDePasse}
          onChange={setAvecMotDePasse}
          locked={!comptesBranches}
          lockedReason={raisonSansComptes}
          aria-label="Creer un mot de passe pour ce nouvel utilisateur"
        />
      </SettingsRow>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Changer l'adresse : une fenetre d'ACTION, comme l'ajout (G6)                */
/* -------------------------------------------------------------------------- */

/**
 * LA FENETRE QUI CHANGE L'ADRESSE D'UNE PERSONNE.
 *
 * CE QU'ELLE DOIT FAIRE COMPRENDRE, et c'est la phrase du milieu : rien de ce
 * que la personne a saisi ne se perd. Ses heures et ses fiches la designent par
 * son identifiant stable (`personId`), pas par son adresse ; elles restent a
 * elle. Seule la porte d'entree change : elle entrera avec la nouvelle adresse,
 * et l'ancienne n'ouvre plus rien.
 *
 * Une adresse deja prise par une autre ligne est refusee ICI, avant l'envoi :
 * l'hote la refuserait aussi, mais une phrase sous le champ vaut mieux qu'un
 * refus apres coup.
 */
function ChangerAdresseModal({
  membre,
  adressesPrises,
  onClose,
  onChanger,
}: {
  membre: MembreVue | null;
  adressesPrises: readonly string[];
  onClose: () => void;
  onChanger: (nouvelle: string) => void;
}): JSX.Element {
  const [adresse, setAdresse] = useState('');

  // Repart a neuf a chaque ouverture : l'adresse tapee pour une autre personne
  // n'attend pas dans le champ.
  useEffect(() => {
    if (membre !== null) setAdresse('');
  }, [membre]);

  const propre = adresse.trim().toLowerCase();
  const valide = emailValide(propre);
  const meme = membre !== null && propre === membre.email;
  const prise = !meme && adressesPrises.includes(propre);
  const nom =
    membre?.displayName != null && membre.displayName !== '' ? membre.displayName : (membre?.email ?? '');
  const probleme =
    adresse === ''
      ? null
      : !valide
        ? 'Adresse email invalide.'
        : meme
          ? "C'est deja son adresse."
          : prise
            ? 'Adresse déjà utilisée.'
            : null;

  return (
    <Modal
      open={membre !== null}
      onClose={onClose}
      title="Changer l'adresse"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            disabled={!valide || meme || prise}
            onClick={() => onChanger(propre)}
          >
            Changer l&apos;adresse
          </Button>
        </>
      }
    >
      <SettingsInfo>{`Adresse actuelle : ${membre?.email ?? ''}`}</SettingsInfo>

      <SettingsRow label="Nouvelle adresse">
        <SettingsInput
          value={adresse}
          onChange={(event) => setAdresse(event.target.value)}
          placeholder="prenom.nom@exemple.lu"
          aria-label="Nouvelle adresse"
        />
      </SettingsRow>
      {probleme !== null && <SettingsMessage tone="error">{probleme}</SettingsMessage>}

      <SettingsInfo>Heures et fiches conservées. L’ancienne adresse perd son accès.</SettingsInfo>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Le mot de passe, montre UNE fois                                            */
/* -------------------------------------------------------------------------- */

/**
 * LA FENETRE QUI MONTRE UN MOT DE PASSE, ET LA SEULE FOIS OU IL EXISTE.
 *
 * Il n'est ecrit NULLE PART : ni en base, ni dans un journal, ni dans l'etat de
 * l'ecran apres fermeture. Refermer cette fenetre le perd, et c'est voulu : un
 * mot de passe qu'on peut relire est un mot de passe qui traine. D'ou le texte
 * qui le dit, et le bouton de copie a portee de main.
 *
 * `Modal` et pas une ligne dans la liste : c'est une fenetre d'ACTION (G6), et
 * il faut que le geste de fermeture soit conscient.
 */
function MotDePasseModal({
  entree,
  onClose,
}: {
  entree: { email: string; valeur: string } | null;
  onClose: () => void;
}): JSX.Element {
  const [copie, setCopie] = useState(false);

  useEffect(() => {
    if (entree === null) setCopie(false);
  }, [entree]);

  return (
    <Modal
      open={entree !== null}
      onClose={onClose}
      title="Mot de passe"
      footer={
        <Button variant="primary" onClick={onClose}>
          Fermer
        </Button>
      }
    >
      <SettingsInfo>Pour {entree?.email ?? ''}. Affiché une seule fois.</SettingsInfo>

      <SettingsCode>{entree?.valeur ?? ''}</SettingsCode>
      <SettingsActions>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            const valeur = entree?.valeur;
            if (valeur == null) return;
            /* `navigator.clipboard` n'existe pas hors contexte securise (une
               IP en http sur le reseau local). Le `catch` n'est donc pas une
               precaution de style : sans lui, le bouton ne ferait rien et ne
               dirait rien. Le mot de passe reste selectionnable a cote. */
            void navigator.clipboard
              ?.writeText(valeur)
              .then(() => setCopie(true))
              .catch(() => setCopie(false));
          }}
        >
          {copie ? 'Copie' : 'Copier'}
        </Button>
      </SettingsActions>
    </Modal>
  );
}

export default MembresSettingsPanel;
